<script setup>
import { ref, computed } from 'vue'
import { router, useForm, usePage, Link } from '@inertiajs/vue3'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Popover from 'primevue/popover'
import DatePicker from 'primevue/datepicker'
import Divider from 'primevue/divider'
import Avatar from 'primevue/avatar'
import Textarea from 'primevue/textarea'
import moment from 'moment'
import { getInitials } from '../../../../helpers.js'
import { trans } from 'laravel-vue-i18n'

const props = defineProps({
    reservation: Object,
    contact: Object,
})

const page = usePage()

const messageForm = useForm({ message: '', sendAs: '' })
const sendMessage = (sendAs) => {
    messageForm.sendAs = sendAs
    messageForm.post(`/app/contacts/${props.contact.id}/send-message`, {
        preserveState: true,
        onSuccess: () => { messageForm.reset() },
    })
}

const msgTypeLabel = {
    sent_email_message: { label: trans('reservations.msg_type_email_sent'), icon: 'pi-envelope', color: 'text-blue-500' },
    sent_sms_message:   { label: trans('reservations.msg_type_sms_sent'),   icon: 'pi-mobile',   color: 'text-green-500' },
    received_message:   { label: trans('reservations.msg_type_received'),   icon: 'pi-comment',  color: 'text-gray-400' },
}

const fmtPrice = (val) =>
    new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2, style: 'currency', currency: 'CZK' }).format(val)

const stateLabel = computed(() => ({
    pending:  trans('reservations.status_pending'),
    accepted: trans('reservations.state_accepted_label'),
    rejected: trans('reservations.state_rejected_label'),
}[props.reservation.state]))
const stateSeverity = computed(() => ({ pending: 'warn', accepted: 'success', rejected: 'danger' }[props.reservation.state]))

const acceptReservation = () => {
    router.get(`/app/forms/event/${props.reservation.id}/accept`, {}, { preserveState: false })
}
const rejectReservation = () => {
    router.get(`/app/forms/event/${props.reservation.id}/reject`, {}, { preserveState: false })
}

const notifyPopover = ref()
const notifyOptions = [
    { type: 'new_client',     label: trans('reservations.notify_new_client') },
    { type: 'confirm_client', label: trans('reservations.notify_confirm') },
    { type: 'cancel_client',  label: trans('reservations.notify_cancel') },
    { type: 'reminder_client',label: trans('reservations.notify_reminder') },
]
const sendNotification = (type) => {
    router.post(`/app/reservations/${props.reservation.id}/notify`, { type }, {
        preserveState: true,
        onSuccess: () => notifyPopover.value.hide(),
    })
}

const formData = computed(() => {
    const fd = JSON.parse(props.reservation.form_data)
    if (Array.isArray(fd) || !fd) {
        return null
    }
    return Object.entries(fd)
})

const totalPrice = computed(() =>
    props.reservation.bill_items?.reduce((s, i) => s + i.unit_price_with_vat * i.amount, 0) ?? 0
)

const showActivityLog = ref(false)

const logLabels = {
    new_reservation:                          trans('reservations.activity_new'),
    confirm_reservation:                      trans('reservations.activity_confirm'),
    cancel_reservation:                       trans('reservations.activity_cancel'),
    change_reservation_email:                 trans('reservations.activity_email_change'),
    confirm_reservation_email:                trans('reservations.activity_email_confirm'),
    change_reservation_sms:                   trans('reservations.activity_sms_change'),
    confirm_reservation_sms:                  trans('reservations.activity_sms_confirm'),
    new_reservation_email:                    trans('reservations.activity_email_new'),
    cancel_reservation_email:                 trans('reservations.activity_email_cancel'),
    new_reservation_sms:                      trans('reservations.activity_sms_new'),
    cancel_reservation_sms:                   trans('reservations.activity_sms_cancel'),
    pending_state_changed:                    trans('reservations.activity_state_pending'),
    accepted_state_changed:                   trans('reservations.activity_state_accepted'),
    rejected_state_changed:                   trans('reservations.activity_state_rejected'),
    change_of_date:                           trans('reservations.activity_date_change'),
    change_of_date_of_time_start_of_time_end: trans('reservations.activity_datetime_change'),
    change_of_time_start_of_time_end:         trans('reservations.activity_time_change'),
    change_of_time_start:                     trans('reservations.activity_time_start_change'),
    change_of_time_end:                       trans('reservations.activity_time_end_change'),
}

const eventChanger = ref()
const dateComputed = computed({
    get: () => new Date(props.reservation.date || '2025-01-01'),
    set: (val) => {
        props.reservation.date = moment(val).format('YYYY-MM-DD')
        props.reservation.formatted_date = moment(val).format('D.MM.yy')
    },
})
const timeStartComputed = computed({
    get: () => {
        const [h, m] = (props.reservation.time_start || '00:00').split(':')
        const d = new Date('2025-01-01T00:00')
        d.setHours(h, m)
        return d
    },
    set: (val) => {
        const hh = String(val.getHours()).padStart(2, '0')
        const mm = String(val.getMinutes()).padStart(2, '0')
        props.reservation.time_start = `${hh}:${mm}:00`
        if (val >= timeEndComputed.value) {
            const end = timeEndComputed.value
            end.setHours(val.getHours() + 1)
            props.reservation.time_end = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}:00`
        }
    },
})
const timeEndComputed = computed({
    get: () => {
        const [h, m] = (props.reservation.time_end || '00:00').split(':')
        const d = new Date('2025-01-01T00:00')
        d.setHours(h, m)
        return d
    },
    set: (val) => {
        const hh = String(val.getHours()).padStart(2, '0')
        const mm = String(val.getMinutes()).padStart(2, '0')
        props.reservation.time_end = `${hh}:${mm}:00`
    },
})
const submitChange = () => {
    eventChanger.value.hide()
    router.post(`/app/forms/event/${props.reservation.id}/change`, {
        date: moment(dateComputed.value).format('YYYY-MM-DD'),
        timeStart: props.reservation.time_start,
        timeEnd: props.reservation.time_end,
        message: null,
    })
}
</script>

<template>
    <div class="flex flex-col gap-4">

        <!-- State + actions — full width -->
        <div class="flex items-center gap-2 flex-wrap">
            <Tag :value="stateLabel" :severity="stateSeverity" />
            <div class="grow"></div>
            <Button :label="$t('reservations.btn_confirm')" size="small" rounded icon="pi pi-check"
                    :severity="reservation.state === 'accepted' ? 'success' : 'secondary'"
                    @click="acceptReservation" />
            <Button :label="$t('reservations.btn_reject')" size="small" rounded icon="pi pi-times"
                    :severity="reservation.state === 'rejected' ? 'danger' : 'secondary'"
                    @click="rejectReservation" />
            <Popover ref="notifyPopover">
                <div class="flex flex-col gap-1 w-44">
                    <p class="text-xs text-gray-400 px-1 pb-1">{{ $t('reservations.notify_type') }}</p>
                    <Button v-for="n in notifyOptions" :key="n.type" :label="n.label"
                            size="small" text severity="secondary" class="justify-start"
                            @click="sendNotification(n.type)" />
                </div>
            </Popover>
        </div>

        <!-- Two-column grid on large screens -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">

            <!-- LEFT column -->
            <div class="flex flex-col gap-4">

                <!-- Booking details card -->
                <div class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 divide-y dark:divide-neutral-700 overflow-hidden">
                    <div class="px-4 py-2 bg-neutral-50 dark:bg-neutral-800">
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('reservations.section_reservation') }}</p>
                    </div>

                    <!-- Date / time -->
                    <div class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-clock text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <div class="grow min-w-0">
                            <p class="text-xs text-gray-400 mb-0.5">{{ $t('reservations.meta_date') }}</p>
                            <p class="text-sm" v-if="reservation.time_start">
                                {{ reservation.formatted_date }}
                                <span class="mx-1 font-mono bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">{{ reservation.time_start.substring(0, 5) }}</span>–<span class="ml-1 font-mono bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">{{ reservation.time_end.substring(0, 5) }}</span>
                            </p>
                            <p class="text-sm" v-else>
                                <span class="font-mono bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">{{ reservation.formatted_date }}</span>
                                <span class="mx-1 text-gray-400">–</span>
                                <span class="font-mono bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">{{ reservation.formatted_date_end }}</span>
                            </p>
                        </div>
                        <Button size="small" text icon="pi pi-pencil" severity="secondary" class="flex-shrink-0"
                                @click="eventChanger.toggle($event)" />
                        <Popover ref="eventChanger">
                            <div class="flex flex-wrap items-center gap-2 p-1">
                                <DatePicker v-model="dateComputed" class="w-[124px]" date-format="dd.mm.yy" fluid />
                                <DatePicker time-only v-model="timeStartComputed" class="w-[72px]" fluid variant="filled" />
                                <span class="text-gray-400">–</span>
                                <DatePicker time-only v-model="timeEndComputed" :min-date="timeStartComputed" class="w-[72px]" fluid variant="filled" />
                                <Button icon="pi pi-check" :label="$t('common.save')" size="small" @click="submitChange" />
                            </div>
                        </Popover>
                    </div>

                    <!-- Rental item -->
                    <div v-if="reservation.rental_items" class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-home text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <div>
                            <p class="text-xs text-gray-400 mb-0.5">{{ $t('reservations.meta_rental_item') }}</p>
                            <p class="text-sm">{{ reservation.rental_items.name }}</p>
                        </div>
                    </div>

                    <!-- Guests -->
                    <div v-if="reservation.adults != null" class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-users text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <div>
                            <p class="text-xs text-gray-400 mb-0.5">{{ $t('reservations.meta_guests') }}</p>
                            <p class="text-sm">
                                {{ reservation.adults }} {{ $t('common.adults') }}<template v-if="reservation.children"> · {{ reservation.children }} {{ $t('common.children') }}</template><template v-if="reservation.pets"> · {{ reservation.pets }} {{ $t('common.pets') }}</template>
                            </p>
                        </div>
                    </div>

                    <!-- Service + worker -->
                    <div v-if="reservation.services?.name" class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-briefcase text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <div>
                            <p class="text-xs text-gray-400 mb-0.5">{{ $t('reservations.meta_service') }}</p>
                            <p class="text-sm">{{ reservation.services.name }}<span v-if="reservation.workers?.name" class="text-gray-400"> — {{ reservation.workers.name }}</span></p>
                        </div>
                    </div>
                </div>

                <!-- Contact card -->
                <div class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 divide-y dark:divide-neutral-700 overflow-hidden">
                    <div class="px-4 py-2 bg-neutral-50 dark:bg-neutral-800">
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('reservations.meta_contact') }}</p>
                    </div>
                    <div class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-user text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <span class="text-sm">{{ reservation.name }}</span>
                    </div>
                    <div class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-envelope text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <a :href="`mailto:${reservation.email}`" class="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate">{{ reservation.email }}</a>
                    </div>
                    <div v-if="reservation.phone" class="flex items-center gap-3 px-4 py-3">
                        <i class="pi pi-phone text-gray-400 text-sm w-4 flex-shrink-0"></i>
                        <a :href="`tel:${reservation.phone}`" class="text-sm hover:underline">{{ reservation.phone }}</a>
                    </div>
                    <div v-if="reservation.message" class="flex items-start gap-3 px-4 py-3">
                        <i class="pi pi-comment text-gray-400 text-sm w-4 flex-shrink-0 mt-0.5"></i>
                        <span class="text-sm text-gray-600 dark:text-gray-300 italic">{{ reservation.message }}</span>
                    </div>
                </div>

                <!-- Client link -->
                <Link v-if="contact" :href="`/app/contacts/${contact.id}`"
                   class="flex items-center gap-3 rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors group">
                    <Avatar :label="getInitials(contact.name)" shape="circle"
                            class="!bg-blue-100 dark:!bg-blue-900 !text-blue-700 dark:!text-blue-200 flex-shrink-0" />
                    <div class="grow min-w-0">
                        <p class="text-xs text-gray-400 mb-0.5">{{ $t('reservations.meta_client') }}</p>
                        <p class="text-sm font-medium truncate">{{ contact.name }}</p>
                    </div>
                    <i class="pi pi-arrow-right text-gray-300 text-xs group-hover:translate-x-0.5 transition-transform"></i>
                </Link>

            </div>

            <!-- RIGHT column -->
            <div class="flex flex-col gap-4">

                <!-- Bill items -->
                <div v-if="reservation.bill_items?.length" class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
                    <div class="px-4 py-2 bg-neutral-50 dark:bg-neutral-800">
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('reservations.section_payment') }}</p>
                    </div>
                    <div class="px-4 py-3 flex flex-col gap-1">
                        <div v-for="item in reservation.bill_items" :key="item.id" class="flex justify-between text-sm py-0.5">
                            <span class="text-gray-700 dark:text-gray-300 mr-4">
                                {{ item.description }}<span v-if="item.amount > 1" class="text-gray-400"> × {{ item.amount }}</span>
                            </span>
                            <span class="flex-shrink-0" :class="item.unit_price_with_vat * item.amount < 0 ? 'text-green-600 dark:text-green-400' : ''">
                                {{ fmtPrice(item.unit_price_with_vat * item.amount) }}
                            </span>
                        </div>
                        <Divider class="!my-1" />
                        <div class="flex justify-between text-sm font-semibold">
                            <span>{{ $t('common.total') }}</span>
                            <span>{{ fmtPrice(totalPrice) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Custom form fields -->
                <div v-if="formData" class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
                    <div class="px-4 py-2 bg-neutral-50 dark:bg-neutral-800">
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('reservations.meta_form') }}</p>
                    </div>
                    <div class="px-4 py-3 flex flex-col gap-2">
                        <div v-for="[key, val] in formData" :key="key" class="flex flex-col gap-0.5 text-sm">
                            <span class="text-xs text-gray-400">{{ key }}</span>
                            <span class="text-gray-800 dark:text-gray-200">{{ Array.isArray(val) ? val.join(', ') : val }}</span>
                        </div>
                    </div>
                </div>

                <!-- Activity log -->
                <div v-if="reservation.activity_logs?.length" class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
                    <button
                        class="w-full flex items-center gap-2 px-4 py-2 bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                        @click="showActivityLog = !showActivityLog"
                    >
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider grow text-left">{{ $t('reservations.section_activity') }} ({{ reservation.activity_logs.length }})</p>
                        <i :class="showActivityLog ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs text-gray-400"></i>
                    </button>
                    <div v-if="showActivityLog" class="divide-y dark:divide-neutral-700">
                        <div v-for="log in reservation.activity_logs" :key="log.id" class="flex items-start gap-3 px-4 py-2.5">
                            <div class="w-1.5 h-1.5 rounded-full bg-blue-300 dark:bg-blue-600 mt-1.5 flex-shrink-0"></div>
                            <div class="min-w-0">
                                <p class="text-sm text-gray-700 dark:text-gray-200">{{ logLabels[log.action] ?? log.action }}</p>
                                <p class="text-xs text-gray-400 mt-0.5">{{ log.date_friendly }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Communication -->
                <div v-if="contact" class="rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
                    <div class="px-4 py-2 bg-neutral-50 dark:bg-neutral-800">
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('reservations.section_communication') }}</p>
                    </div>

                    <!-- Composer -->
                    <div class="p-3 border-b dark:border-neutral-700">
                        <div class="border dark:border-neutral-700 rounded-xl p-3 bg-white dark:bg-neutral-800 focus-within:ring-2 ring-blue-400 ring-offset-1">
                            <Textarea
                                v-model="messageForm.message"
                                class="w-full resize-none outline-none bg-transparent text-sm dark:text-white"
                                :placeholder="$t('reservations.msg_compose_placeholder')"
                                rows="3"
                                auto-resize
                                unstyled
                            />
                            <small v-if="messageForm.errors.message" class="text-red-500 text-xs block mt-1">{{ messageForm.errors.message }}</small>
                            <div class="flex justify-end gap-2 mt-2">
                                <Button size="small" severity="secondary" icon="pi pi-envelope" label="E-mail"
                                        :loading="messageForm.processing && messageForm.sendAs === 'email'"
                                        @click="sendMessage('email')" />
                                <Button v-if="reservation.phone" size="small" icon="pi pi-mobile" label="SMS"
                                        :loading="messageForm.processing && messageForm.sendAs === 'sms'"
                                        :disabled="page.props.user.credit_amount === 0"
                                        v-tooltip.bottom="page.props.user.credit_amount === 0 ? $t('common.add_credits') : null"
                                        @click="sendMessage('sms')" />
                            </div>
                        </div>
                    </div>

                    <!-- Message log -->
                    <div v-if="contact.log_messages?.length" class="divide-y dark:divide-neutral-700 max-h-72 overflow-y-auto">
                        <div v-for="msg in contact.log_messages" :key="msg.id" class="px-4 py-3">
                            <div class="flex items-center gap-2 mb-1">
                                <i :class="['pi text-xs', msgTypeLabel[msg.type]?.icon, msgTypeLabel[msg.type]?.color]"></i>
                                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ msgTypeLabel[msg.type]?.label }}</span>
                                <span class="text-xs text-gray-300 dark:text-gray-600 ml-auto">{{ msg.human_diff }}</span>
                            </div>
                            <div v-if="msg.message" class="text-sm text-gray-700 dark:text-gray-300 bg-neutral-50 dark:bg-neutral-800 rounded-lg px-3 py-2" v-html="msg.message"></div>
                        </div>
                    </div>
                    <div v-else class="px-4 py-6 text-center text-sm text-gray-400">
                        {{ $t('reservations.no_communication') }}
                    </div>
                </div>

                <!-- Reservation form link -->
                <Link :href="`/app/forms/${reservation.reservation_forms.slug}`"
                   class="flex items-center gap-3 rounded-2xl border dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors group">
                    <i class="pi pi-calendar text-gray-400 text-sm w-4"></i>
                    <span class="text-sm text-gray-600 dark:text-gray-300 grow">{{ reservation.reservation_forms.name }}</span>
                    <i class="pi pi-arrow-right text-gray-300 text-xs group-hover:translate-x-0.5 transition-transform"></i>
                </Link>

            </div>
        </div>
    </div>
</template>
